import { Link } from "react-router-dom";
import WymhPlatformCard from "@/pages/download/components/wymh-platform-card";
import { wymhPlatforms } from "@/mocks/wymh-content";
import { useWymhSiteConfig } from "@/hooks/wymh-site-config";
import { useWymhSeo } from "@/hooks/wymh-seo";

const WYMH_STEPS = [
  { icon: "ri-download-2-line", title: "选择你的设备", desc: "在下面找到对应系统版本，点击下载按钮，浏览器会自动开始下载安装包，全程走官方地址。" },
  { icon: "ri-shield-check-line", title: "允许安装", desc: "Android 与 HarmonyOS 首次安装如提示来源未知，请在设置中允许后再次点击安装。" },
  { icon: "ri-login-circle-line", title: "登录同步", desc: "用同一账号登录，书架、收藏与阅读进度会自动同步，换设备无缝续读。" },
];

export default function Download() {
  const config = useWymhSiteConfig();
  const links = config.downloadLinks as unknown as Record<string, string>;

  const getLink = (key: string) => links[key] || links.android || config.domainUrl;

  useWymhSeo({
    title: "五一漫画官方正版入口-五一漫画官网免费版APP下载-五一漫画",
    keywords: "五一漫画官方正版入口,五一漫画官网免费版APP下载",
    description:
      "五一漫画APP官方下载，正版安装包免费安全。聚合海量热门国漫日漫，高清全彩更新快，支持离线缓存，安卓苹果均可下载安装，即刻畅享追漫乐趣。",
    canonical: "https://www.wuyicomic.com/download",
  });

  return (
    <div className="w-full min-h-screen bg-background-50">
      <main>
        <section className="relative w-full bg-foreground-950 pt-16 md:pt-24 pb-16 md:pb-20 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full wymh-halftone-dark opacity-50"></div>
          <div
            className="absolute top-0 left-0 w-full h-full"
            style={{ backgroundImage: "linear-gradient(120deg, rgba(16,11,13,0.96) 0%, rgba(69,6,29,0.92) 100%)" }}
          ></div>

          <div className="relative w-full px-4 md:px-8">
            <div className="flex flex-col items-center text-center">
              <span className="w-20 h-20 md:w-24 md:h-24 rounded-[24px] overflow-hidden bg-foreground-900 border border-primary-500/30 mb-6 flex items-center justify-center">
                <img
                  src="/images/wymh-app-icon.png"
                  alt="五一漫画App图标"
                  title="五一漫画官方图标"
                  className="w-full h-full object-top"
                />
              </span>

              <h1 className="wymh-heading text-background-50 text-5xl md:text-7xl leading-none mb-5">下载五一漫画</h1>
              <p className="text-background-100/80 text-base md:text-lg leading-8 max-w-2xl mb-10">
                完整客户端支持 Android、iOS、HarmonyOS、Windows 四个版本，账号互通、进度云端同步。选好你的设备，一键开始追更。
              </p>

              <div className="w-full max-w-3xl grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {wymhPlatforms.map((platform) => (
                  <a
                    key={platform.key}
                    href={getLink(platform.key)}
                    className="flex flex-col items-center gap-2 px-4 py-5 rounded-2xl bg-background-50/10 border border-background-50/20 hover:border-primary-400/60 hover:bg-background-50/15 cursor-pointer"
                  >
                    <i className={`${platform.icon} text-3xl text-primary-300`}></i>
                    <span className="text-background-50 text-sm font-bold whitespace-nowrap">
                      {platform.name.replace(" 版", "")}
                    </span>
                    <span className="text-[11px] text-background-100/60 whitespace-nowrap">点击下载</span>
                  </a>
                ))}
              </div>

              <Link
                to="/"
                className="inline-flex items-center gap-2 text-background-100/70 hover:text-primary-300 text-sm mt-8 cursor-pointer"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                返回首页
              </Link>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-16 md:py-20">
          <div className="w-full px-4 md:px-8">
            <div className="max-w-3xl mb-10">
              <h2 className="wymh-heading text-foreground-950 text-3xl md:text-4xl mb-3">按系统选择版本</h2>
              <p className="text-foreground-600 text-base leading-8">
                每个版本都是完整的阅读客户端，功能一致、体验统一。不确定选哪个的话，手机就选 Android / iOS / HarmonyOS，电脑选 Windows 版。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {wymhPlatforms.map((platform) => (
                <WymhPlatformCard key={platform.key} platform={platform} />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-16 md:py-20">
          <div className="w-full px-4 md:px-8">
            <div className="max-w-3xl mb-10">
              <h2 className="wymh-heading text-foreground-950 text-3xl md:text-4xl mb-3">三步装好，马上开追</h2>
              <p className="text-foreground-600 text-base leading-8">从下载到翻开第一话，通常不到一分钟。</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {WYMH_STEPS.map((step, index) => (
                <div key={step.title} className="rounded-2xl bg-background-100 border border-background-200 p-6 md:p-7">
                  <div className="flex items-center justify-between mb-5">
                    <span className="w-12 h-12 rounded-xl bg-primary-500 text-background-50 flex items-center justify-center">
                      <i className={`${step.icon} text-2xl`}></i>
                    </span>
                    <span className="wymh-heading text-primary-200 text-4xl leading-none">0{index + 1}</span>
                  </div>
                  <h3 className="text-foreground-950 font-bold text-lg mb-2">{step.title}</h3>
                  <p className="text-foreground-600 text-sm leading-7">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}